<template>
    <div
        class="no-scrollbar flex items-center justify-start gap-5 overflow-x-scroll pb-2"
        v-bind="$attrs"
    >
        <div v-for="match in allMatches" :key="match.id">
            <div
                class="bg-card border-stroke-1 hover:bg-card-2 min-w-50 cursor-pointer border-1 p-4 pb-2.5 transition-colors"
                data-testid="recent-match-card"
                :data-match-id="match.id"
                @click="handleMatchClick(match)"
            >
                <div class="flex gap-5">
                    <!-- Team 1 -->
                    <div class="relative w-12">
                        <div class="relative">
                            <template
                                v-for="(profile, index) in getTeam1Profiles(match)"
                                :key="profile.id"
                            >
                                <div
                                    class="absolute"
                                    :style="{ left: index === 0 ? '0' : '20%' }"
                                    :class="[
                                        profile.isWinner
                                            ? 'bg-gradient-to-br from-emerald-500 to-stone-900/0'
                                            : 'bg-gradient-to-br from-red-600 to-stone-900/0',
                                    ]"
                                >
                                    <Avatar
                                        :size="12"
                                        :src="profile.avatar"
                                        :alt="profile.tag_name"
                                    />
                                </div>
                            </template>
                        </div>
                        <div
                            v-if="
                                getTeam1Profiles(match).length > 0 &&
                                !isSoloGame(match) &&
                                isMatchComplete(match)
                            "
                            class="bg-card-2 absolute -bottom-4 left-2 flex h-8 w-8 items-center justify-center rounded-full border-2 p-2 text-xl"
                            :class="[
                                getTeam1Profiles(match)[0].isWinner
                                    ? 'border-green-500'
                                    : 'border-red-600',
                            ]"
                        >
                            <p class="pt-0.5">
                                {{ getTeamScore(match, getTeam1Profiles(match)[0].team) }}
                            </p>
                        </div>
                    </div>

                    <!-- Team 2 (hide for solo games) -->
                    <div v-if="!isSoloGame(match)" class="relative w-12">
                        <div class="relative">
                            <template
                                v-for="(profile, index) in getTeam2Profiles(match)"
                                :key="profile.id"
                            >
                                <div
                                    class="absolute"
                                    :style="{ left: index === 0 ? '0' : '20%' }"
                                    :class="[
                                        profile.isWinner
                                            ? 'bg-gradient-to-br from-emerald-500 to-stone-900/0'
                                            : 'bg-gradient-to-br from-red-600 to-stone-900/0',
                                    ]"
                                >
                                    <Avatar
                                        :size="12"
                                        :src="profile.avatar"
                                        :alt="profile.tag_name"
                                    />
                                </div>
                            </template>
                        </div>
                        <div
                            v-if="getTeam2Profiles(match).length > 0 && isMatchComplete(match)"
                            class="bg-card-2 absolute -bottom-4 left-2 flex h-8 w-8 items-center justify-center rounded-full border-2 p-2 text-xl"
                            :class="[
                                getTeam2Profiles(match)[0].isWinner
                                    ? 'border-green-500'
                                    : 'border-red-600',
                            ]"
                        >
                            <p class="pt-0.5">
                                {{ getTeamScore(match, getTeam2Profiles(match)[0].team) }}
                            </p>
                        </div>
                    </div>

                    <div
                        class="text-titles text-bold flex flex-col items-center justify-center text-center text-xs leading-none font-bold"
                    >
                        <template v-if="isMatchComplete(match)">
                            <p class="pb-2 whitespace-nowrap">
                                {{ formatMatchDate(match.matchend_at) }}
                            </p>
                            <p class="whitespace-nowrap">
                                {{ formatMatchTime(match.matchend_at) }}
                            </p>
                        </template>
                        <template v-else>
                            <p class="pb-2 whitespace-nowrap">
                                {{ formatMatchDate(match.created_at) }}
                            </p>
                            <p class="whitespace-nowrap">{{ formatMatchTime(match.created_at) }}</p>
                        </template>
                    </div>
                </div>

                <div
                    class="grid w-full content-center gap-5 pt-6"
                    :class="isMatchComplete(match) ? 'grid-cols-3' : 'grid-cols-[1fr_auto]'"
                >
                    <div
                        class="flex items-center justify-center gap-3 text-center text-sm font-bold"
                        :class="isMatchComplete(match) ? 'col-span-2' : ''"
                    >
                        <h4>
                            {{
                                isMatchComplete(match) || !canEndMatch(match)
                                    ? getMatchTypeName(match)
                                    : $t('recent_games.resume')
                            }}
                        </h4>

                        <template v-if="!isMatchComplete(match) && canEndMatch(match)">
                            <span class="text-titles">|</span>
                            <button @click.stop="handleEndMatch(match.id)" :title="$t('recent_games.end_match_title')"  data-testid="recent-match-end">
                                {{ $t('recent_games.end') }}
                            </button>
                        </template>
                    </div>

                    <div class="ml-auto">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            v-if="
                                canEndMatch(match) ||
                                (!canEndMatch(match) && isMatchComplete(match))
                            "
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M13.5856 11.9999L8.29274 17.2928L9.70696 18.707L16.4141 11.9999L9.70695 5.29282L8.29274 6.70703L13.5856 11.9999Z"
                                fill="#797979"
                            />
                        </svg>
                        <span class="text-xs" v-else>{{ $t('recent_games.ongoing') }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <Modal
        :is-visible="showEndMatchModal"
        :on-confirm="confirmEndMatch"
        :on-decline="cancelEndMatch"
        :is-loading="isEndingMatch"
    >
        <h2 class="font-title text-base leading-normal font-extrabold text-white uppercase">
            {{ $t('recent_games.end_match_title') }}
        </h2>
        <p class="mt-4 text-sm leading-normal text-white">
            {{ $t('recent_games.end_match_body') }}
        </p>
    </Modal>
</template>

<script>
import { computed, ref } from 'vue'
import { useAuthStore } from '@/stores/auth.js'
import { useMatchStore } from '@/stores/match.js'
import { formatMatchDate, formatMatchTime } from '@/utils/dateHelpers.js'
import { useRouter } from 'vue-router'
import Avatar from '@/components/Avatar.vue'
import Modal from '@/components/Modal.vue'
import { MATCH_TYPE_NAMES } from '@/constants/matchTypes.js'

export default {
    name: 'GamesOverview',
    components: {
        Avatar,
        Modal,
    },
    inheritAttrs: false,
    setup() {
        const authStore = useAuthStore()
        const matchStore = useMatchStore()
        const router = useRouter()
        const showEndMatchModal = ref(false)
        const matchToEnd = ref(null)
        const isEndingMatch = ref(false)

        const allMatches = computed(() => {
            const active = authStore.active_matches || []
            const recent = authStore.recent_matches || []
            return [...active, ...recent]
        })

        const isMatchComplete = (match) => {
            return !!match.matchend_at
        }

        const getMatchTypeName = (match) => {
            return MATCH_TYPE_NAMES[match.matchtype?.id] || match.matchtype?.name
        }

        const isSoloGame = (match) => {
            return match?.profiles?.length === 1
        }

        const canEndMatch = (match) => {
            return match.creator_profile_id === authStore.profile?.id
        }

        const handleMatchClick = (match) => {
            if (isMatchComplete(match)) {
                router.push(`/matches/${match.id}/game-stats`)
            } else if (canEndMatch(match)) {
                router.push(`/matches/${match.id}/play`)
            }
        }

        const getMatchProfiles = (match) => {
            if (!match?.profiles || !Array.isArray(match.profiles)) {
                return []
            }

            const winnerId = match.matchdata?.winner_id

            return match.profiles.map((profile) => {
                const matchProfile = match.matchdata?.profiles?.find(
                    (p) => p.id === profile.profile_id,
                )
                const isWinner = winnerId === (matchProfile?.team || profile.team)

                return {
                    id: profile.profile_id || profile.id,
                    tag_name: profile.tag_name,
                    avatar: profile.avatar,
                    isWinner,
                    sets_won: matchProfile?.sets_won || 0,
                    legs_won: matchProfile?.legs_won || 0,
                    team: matchProfile?.team || profile.team || profile.profile_id,
                }
            })
        }

        const getTeam1Profiles = (match) => {
            const profiles = getMatchProfiles(match)
            if (profiles.length === 0) return []
            const team1Id = profiles[0].team
            return profiles.filter((p) => p.team === team1Id)
        }

        const getTeam2Profiles = (match) => {
            const profiles = getMatchProfiles(match)
            if (profiles.length === 0) return []
            const team1Id = profiles[0].team
            return profiles.filter((p) => p.team !== team1Id)
        }

        const getTeamScore = (match, teamId) => {
            if (!match?.matchdata?.profiles) return 0
            const profiles = match.matchdata.profiles.filter((p) => p.team === teamId)
            if (profiles.length === 0) return 0
            return match.settings?.sets_to_win > 1 ? profiles[0].sets_won : profiles[0].legs_won
        }

        const navigateToGameStats = (matchId) => {
            if (this.isMatchComplete(matchId)) {
                router.push(`/matches/${matchId}/game-stats`)
            }
        }

        const resumeMatch = (matchId) => {
            router.push(`/matches/${matchId}/play`)
        }

        const handleEndMatch = (matchId) => {
            matchToEnd.value = matchId
            showEndMatchModal.value = true
        }

        const confirmEndMatch = async () => {
            if (!matchToEnd.value || isEndingMatch.value) return

            isEndingMatch.value = true

            try {
                const response = await matchStore.endMatch(matchToEnd.value)

                if (response.success) {
                    await authStore.verifyToken()
                    showEndMatchModal.value = false
                    matchToEnd.value = null
                }
            } catch (error) {
                console.error('Failed to end match:', error)
            } finally {
                isEndingMatch.value = false
            }
        }

        const cancelEndMatch = () => {
            showEndMatchModal.value = false
            matchToEnd.value = null
        }

        return {
            authStore,
            allMatches,
            isMatchComplete,
            getMatchTypeName,
            isSoloGame,
            canEndMatch,
            getTeam1Profiles,
            getTeam2Profiles,
            getTeamScore,
            formatMatchDate,
            formatMatchTime,
            navigateToGameStats,
            resumeMatch,
            handleEndMatch,
            showEndMatchModal,
            confirmEndMatch,
            cancelEndMatch,
            isEndingMatch,
            handleMatchClick,
        }
    },
}
</script>
